// ===========================================
// Meet & Greet notes: the form's rules and labels (#255, enquiries Phase 4)
// ===========================================
// Mirrors `validators/meetAndGreetNotes.validators.ts` (lengths and the ATTENDED / NO_SHOW rules).
// The server stays the authority; this only saves a round trip.

import type {
  NoteAttendance,
  NoteEngagement,
  NoteOutcome,
  RecordMeetAndGreetNotesInput,
} from '../api/meetAndGreetNotes.api';
import { formatInSchoolTz } from './dateUtils';

/** `dd/mm/yyyy` with the weekday and time, in the school's zone (en-AU). */
export function formatMeetingTime(iso: string | null, timeZone: string): string {
  if (!iso) return 'No time set yet';
  return formatInSchoolTz(iso, timeZone, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export const NOTES_LIMITS = {
  priorExperience: 500,
  observations: 5000,
  recommendedProgram: 200,
  recommendedClass: 200,
  notesForTeam: 5000,
} as const;

export const ATTENDANCE_LABELS: Record<NoteAttendance, string> = {
  ATTENDED: 'The family attended',
  NO_SHOW: 'The family did not come (no-show)',
};

export const ENGAGEMENT_LABELS: Record<NoteEngagement, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const OUTCOME_LABELS: Record<NoteOutcome, string> = {
  FOLLOW_UP: 'Needs a follow-up',
  YES_ON_THE_DAY: 'Said yes on the day',
  NOT_SUITABLE: 'Not suitable',
};

export interface NotesFormState {
  attendance: NoteAttendance | '';
  engagement: NoteEngagement | '';
  priorExperience: string;
  observations: string;
  recommendedProgram: string;
  recommendedClass: string;
  notesForTeam: string;
  outcome: NoteOutcome | '';
}

export const EMPTY_NOTES_FORM: NotesFormState = {
  attendance: '',
  engagement: '',
  priorExperience: '',
  observations: '',
  recommendedProgram: '',
  recommendedClass: '',
  notesForTeam: '',
  outcome: '',
};

export type NotesFormErrors = Partial<Record<keyof NotesFormState, string>>;

export function validateNotesForm(form: NotesFormState): NotesFormErrors {
  const errors: NotesFormErrors = {};
  if (!form.attendance) {
    errors.attendance = 'Say whether the family attended';
    return errors;
  }
  // Measured the way the server measures (trimmed), and only the fields the chosen attendance SENDS
  // (a NO_SHOW drops the rest in `buildNotesBody`, so a hidden over-long one must not block Save).
  const sent: Array<keyof typeof NOTES_LIMITS> =
    form.attendance === 'NO_SHOW'
      ? ['observations', 'notesForTeam']
      : ['priorExperience', 'observations', 'recommendedProgram', 'recommendedClass', 'notesForTeam'];
  for (const key of sent) {
    if (form[key].trim().length > NOTES_LIMITS[key]) {
      errors[key] = `Must be ${NOTES_LIMITS[key]} characters or less`;
    }
  }
  if (form.attendance === 'ATTENDED') {
    if (!form.observations.trim()) errors.observations = 'Observations are required when the family attended';
    if (!form.outcome) errors.outcome = 'Choose an outcome';
  }
  return errors;
}

const orNull = (value: string): string | null => value.trim() || null;

/**
 * The POST body. A NO_SHOW carries only the observations and the team note, and NO `outcome` key at
 * all (the server answers 400 to a no-show with an outcome).
 */
export function buildNotesBody(form: NotesFormState): RecordMeetAndGreetNotesInput {
  if (form.attendance === 'NO_SHOW') {
    return {
      attendance: 'NO_SHOW',
      observations: orNull(form.observations),
      notesForTeam: orNull(form.notesForTeam),
    };
  }
  return {
    attendance: 'ATTENDED',
    engagement: form.engagement || null,
    priorExperience: orNull(form.priorExperience),
    observations: orNull(form.observations),
    recommendedProgram: orNull(form.recommendedProgram),
    recommendedClass: orNull(form.recommendedClass),
    notesForTeam: orNull(form.notesForTeam),
    outcome: form.outcome || undefined,
  };
}

/**
 * Where clicking a Meet & Greet on the calendar goes. The teacher page is the booking's own teacher's
 * (anyone else gets a 404 from it); the admin page needs `meet_and_greet.read`. In the teacher view
 * only the teacher page is ever offered. `null` = nowhere to go (no button).
 */
export function meetAndGreetEventTarget(args: {
  bookingId: string | undefined;
  eventTeacherId: string | undefined;
  basePath: string;
  isTeacherView: boolean;
  myTeacherId: string | undefined;
  canReadMeetAndGreet: boolean;
}): string | null {
  const { bookingId, eventTeacherId, basePath, isTeacherView, myTeacherId, canReadMeetAndGreet } = args;
  if (!bookingId) return null;
  if (!isTeacherView && canReadMeetAndGreet) {
    return `${basePath}/meet-and-greet?open=${encodeURIComponent(bookingId)}`;
  }
  if (myTeacherId && eventTeacherId === myTeacherId) {
    const teacherBase = basePath.replace(/\/admin$/, '/teacher');
    return `${teacherBase}/meet-and-greets/${encodeURIComponent(bookingId)}`;
  }
  return null;
}

/** Statuses in which the server accepts notes (mirrors `NOTES_RECORDABLE_STATUSES`). */
export const NOTES_RECORDABLE_STATUSES = ['PENDING_APPROVAL', 'APPROVED', 'WAITLISTED', 'CONVERTED'] as const;

/**
 * The admin side has no `canRecordNotes` from the server: the booking is confirmed (or waitlisted) and
 * the meeting has started. "Notes missing" is the server's `notesMissing`, never computed here. The server re-checks under a lock and answers with a code.
 */
export function adminMayRecordNotes(
  booking: { status: string; scheduledDateTime: string | null } | undefined,
  now: Date = new Date(),
): boolean {
  if (!booking?.scheduledDateTime) return false;
  if (!(NOTES_RECORDABLE_STATUSES as readonly string[]).includes(booking.status)) return false;
  return new Date(booking.scheduledDateTime).getTime() <= now.getTime();
}

/** What a teacher sees for a booking's status (the teacher statuses; anything else shows as is). */
export function teacherStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING_VERIFICATION':
      return 'Waiting for family';
    case 'PENDING_APPROVAL':
      return 'Pending';
    case 'APPROVED':
      return 'Scheduled';
    case 'WAITLISTED':
      return 'Waitlisted';
    case 'CONVERTED':
      return 'Joined';
    default:
      return status.replace(/_/g, ' ').toLowerCase();
  }
}
